<template>
	<Radar
		v-if="isPopulated"
		:chart-options="enhancedChartOptions"
		:chart-data="chartData"
		:chart-id="chartId"
		:dataset-id-key="datasetIdKey"
		:plugins="plugins"
		:css-classes="cssClasses"
		:styles="styles"
		:width="width"
		:height="height"
	/>
	<ChartState
		v-else
		:variant="chartState"
		:height="height"
		:title="stateTitle"
		:description="stateDescription"
		:icon="stateIcon"
		:ghost-shape="ghostShape"
		:ghost-series="ghostSeries"
		v-on="$listeners"
	/>
</template>

<script>
import { Radar } from 'vue-chartjs/legacy'

import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	PointElement,
	LineElement,
	RadialLinearScale
} from 'chart.js'
import { createChartStateMixin } from './chart-state-mixin.js'

ChartJS.register(
	Title,
	Tooltip,
	Legend,
	PointElement,
	RadialLinearScale,
	LineElement
)

export default {
	name: 'radar-chart',
	components: {
		Radar
	},
	// Radar spokes come from the labels, so without them nothing draws. The
	// ring is the nearest circular stand-in; there is no dedicated radar shape.
	mixins: [createChartStateMixin({ ghostShape: 'pie' })],
	props: {
		chartId: {
			type: String,
			default: 'radar-chart'
		},
		datasetIdKey: {
			type: String,
			default: 'label'
		},
		width: {
			type: Number,
			default: 400
		},
		height: {
			type: Number,
			default: 400
		},
		cssClasses: {
			default: '',
			type: String
		},
		styles: {
			type: Object,
			default: () => {}
		},
		plugins: {
			type: Array,
			default: () => []
		},
		chartData: {
			type: Object
		},
		chartOptions: {
			type: Object
		}
	},
	computed: {
		enhancedChartOptions () {
			const isDark = this.$vuetify?.theme?.dark || false
			const legendColor = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'
			const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
			const tickColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'

			const baseOptions = {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					r: {
						angleLines: { color: gridColor },
						grid: { color: gridColor },
						pointLabels: { color: tickColor },
						ticks: { color: tickColor, backdropColor: 'transparent' }
					}
				},
				plugins: {
					legend: {
						position: 'bottom',
						labels: {
							usePointStyle: true,
							pointStyle: 'circle',
							padding: 12,
							color: legendColor,
							font: {
								size: 11
							},
							boxWidth: 10,
							boxHeight: 10
						}
					},
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.9)',
						titleColor: 'white',
						bodyColor: 'white',
						borderColor: 'rgba(255, 255, 255, 0.2)',
						borderWidth: 1,
						cornerRadius: 6,
						displayColors: true,
						usePointStyle: true
					}
				}
			}

			// Deep merge with user-provided options
			return this.deepMerge(baseOptions, this.chartOptions || {})
		}
	},
	created () {
		// Set global font defaults for Chart.js
		ChartJS.defaults.font.family = 'Inter, sans-serif'
		ChartJS.defaults.font.size = 12
		ChartJS.defaults.font.weight = '400'
	},
	methods: {
		deepMerge (target, source) {
			const result = { ...target }
			for (const key in source) {
				if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
					result[key] = this.deepMerge(result[key] || {}, source[key])
				} else {
					result[key] = source[key]
				}
			}
			return result
		}
	}
}
</script>
